import { useEffect, useState, useCallback } from "react";
import { LogOut, Search, Save, Ban, Pin as PinIcon, Sliders, Eye, Trash2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  adminVerify,
  adminGetConfig,
  adminPutConfig,
  adminVendors,
  adminPaddles,
  adminPreview,
} from "@/lib/api";
import { QUESTIONS, DEFAULT_ANSWERS } from "@/lib/quizConfig";

const LS_KEY = "cd_admin_token";

// ---------- Login gate ----------

const Login = ({ onOk }) => {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminVerify(token);
      localStorage.setItem(LS_KEY, token);
      onOk(token);
    } catch {
      toast.error("Invalid token");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cd-container" style={{ maxWidth: 420 }} data-testid="admin-login">
      <div className="cd-card" style={{ padding: 28 }}>
        <h1 className="cd-h2" style={{ marginBottom: 6 }}>Admin sign in</h1>
        <p className="cd-sub" style={{ marginBottom: 20 }}>
          Paste your admin token to manage brand rules, pinned picks, and paddle overrides.
        </p>
        <form onSubmit={submit}>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="cd-input"
            data-testid="admin-token-input"
            style={{
              width: "100%",
              padding: "12px 14px",
              border: "1.5px solid var(--cd-border)",
              borderRadius: 10,
              fontSize: 14,
              marginBottom: 14,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            }}
          />
          <button
            className="cd-btn-primary"
            style={{ width: "100%" }}
            disabled={busy || !token}
            data-testid="admin-login-btn"
          >
            {busy ? "Verifying…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ---------- Vendor allow/block ----------

const VendorList = ({ vendors, config, setConfig }) => {
  const toggle = (vendor, kind) => {
    setConfig((c) => {
      const key = kind === "block" ? "blocked_vendors" : "allowed_vendors";
      const set = new Set(c[key]);
      set.has(vendor) ? set.delete(vendor) : set.add(vendor);
      return { ...c, [key]: [...set] };
    });
  };
  const blockedSet = new Set(config.blocked_vendors);
  const allowedSet = new Set(config.allowed_vendors);

  return (
    <div>
      <div className="cd-sub" style={{ marginBottom: 12 }}>
        {allowedSet.size > 0
          ? `Only paddles from these vendors are shown to shoppers (${allowedSet.size} allowed).`
          : "All vendors are shown by default. Add vendors to Allowed to restrict, or Blocked to hide."}
      </div>
      <div style={{ maxHeight: 480, overflowY: "auto", border: "1px solid var(--cd-border)", borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead style={{ position: "sticky", top: 0, background: "var(--cd-surface)" }}>
            <tr>
              <th style={{ textAlign: "left", padding: 10 }}>Vendor</th>
              <th style={{ textAlign: "right", padding: 10 }}>Paddles</th>
              <th style={{ textAlign: "center", padding: 10 }}>Allowed</th>
              <th style={{ textAlign: "center", padding: 10 }}>Blocked</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((v) => (
              <tr key={v.vendor} style={{ borderTop: "1px solid var(--cd-border)" }} data-testid={`vendor-row-${v.vendor}`}>
                <td style={{ padding: 10, fontWeight: 500 }}>{v.vendor}</td>
                <td style={{ padding: 10, textAlign: "right", color: "var(--cd-muted)" }}>{v.count}</td>
                <td style={{ padding: 10, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={allowedSet.has(v.vendor)}
                    onChange={() => toggle(v.vendor, "allow")}
                    data-testid={`vendor-allow-${v.vendor}`}
                  />
                </td>
                <td style={{ padding: 10, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={blockedSet.has(v.vendor)}
                    onChange={() => toggle(v.vendor, "block")}
                    data-testid={`vendor-block-${v.vendor}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Pinned picks ----------

const PERSONA_KEYS = ["experience", "play_style", "primary_goal", "performance_priority"];

const PinsTab = ({ pins, setPins, allPaddlesById }) => {
  const [pid, setPid] = useState("");
  const [conditions, setConditions] = useState({});
  const addPin = () => {
    if (!pid) return toast.error("Enter a product ID");
    const paddle = allPaddlesById.get(pid);
    if (!paddle) return toast.error("Product ID not found in catalog");
    setPins([...pins, { product_id: pid, match: { ...conditions }, note: paddle.title }]);
    setPid("");
    setConditions({});
  };
  const remove = (idx) => setPins(pins.filter((_, i) => i !== idx));

  const questionsByKey = Object.fromEntries(QUESTIONS.map((q) => [q.id, q]));

  return (
    <div>
      <div className="cd-sub" style={{ marginBottom: 12 }}>
        A pinned paddle is force-included at the top of results whenever the shopper&apos;s answers
        match ALL the conditions you set. Leave conditions blank to always pin.
      </div>

      <div className="cd-card" style={{ padding: 16, marginBottom: 16, background: "var(--cd-surface)" }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Add new pin</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
          <input
            value={pid}
            onChange={(e) => setPid(e.target.value.trim())}
            placeholder="Product ID (from Paddles tab)"
            data-testid="pin-product-id"
            style={{ padding: 10, border: "1.5px solid var(--cd-border)", borderRadius: 8, fontFamily: "ui-monospace, monospace", fontSize: 13 }}
          />
          {pid && allPaddlesById.get(pid) && (
            <div className="cd-micro" style={{ color: "var(--cd-primary)" }}>
              → {allPaddlesById.get(pid).title}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
            {PERSONA_KEYS.map((key) => (
              <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="cd-micro">{key.replace(/_/g, " ")}</span>
                <select
                  value={conditions[key] || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setConditions((c) => {
                      const nc = { ...c };
                      if (v) nc[key] = v;
                      else delete nc[key];
                      return nc;
                    });
                  }}
                  data-testid={`pin-condition-${key}`}
                  style={{ padding: 8, border: "1.5px solid var(--cd-border)", borderRadius: 8, fontSize: 13 }}
                >
                  <option value="">Any</option>
                  {questionsByKey[key]?.options.map((o) => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <button className="cd-btn-primary" onClick={addPin} data-testid="pin-add-btn">
            <Plus size={16} /> Add pin
          </button>
        </div>
      </div>

      <div style={{ fontWeight: 600, marginBottom: 8 }}>Active pins ({pins.length})</div>
      {pins.length === 0 && <div className="cd-micro">No pins yet.</div>}
      <div style={{ display: "grid", gap: 8 }}>
        {pins.map((p, i) => (
          <div key={i} className="cd-card" style={{ padding: 12, display: "flex", gap: 10, alignItems: "center" }} data-testid={`pin-row-${i}`}>
            <PinIcon size={16} color="var(--cd-primary)" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.note || p.product_id}
              </div>
              <div className="cd-micro" style={{ color: "var(--cd-muted)" }}>
                {Object.keys(p.match || {}).length === 0
                  ? "Always pinned"
                  : Object.entries(p.match).map(([k, v]) => `${k}=${v}`).join(" · ")}
              </div>
            </div>
            <button className="cd-btn-ghost" onClick={() => remove(i)} data-testid={`pin-remove-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Paddles table + overrides + block-by-id ----------

const OZ_TO_G = 28.3495;

const PaddlesTab = ({ token, config, setConfig, onLoaded }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminPaddles(token, { q, limit: 50 });
      setRows(data.paddles || []);
      onLoaded?.(data.paddles || []);
    } catch {
      toast.error("Failed to load paddles");
    } finally {
      setLoading(false);
    }
  }, [q, token, onLoaded]);

  useEffect(() => { search(); }, []); // initial

  const blockedSet = new Set(config.blocked_product_ids);
  const toggleBlock = (pid) => {
    setConfig((c) => {
      const s = new Set(c.blocked_product_ids);
      s.has(pid) ? s.delete(pid) : s.add(pid);
      return { ...c, blocked_product_ids: [...s] };
    });
  };

  const startEdit = (p) => {
    const ov = config.attribute_overrides[p.product_id] || {};
    const a = p.attributes || {};
    setEditingId(p.product_id);
    setDraft({
      weight_oz: ov.weight_oz ?? a.weight_oz ?? "",
      thickness_mm: ov.thickness_mm ?? a.thickness_mm ?? "",
      core: ov.core ?? a.core ?? "",
      surface: ov.surface ?? a.surface ?? "",
      control: ov.play_style_scores?.control ?? a.play_style_scores?.control ?? 0.5,
      power: ov.play_style_scores?.power ?? a.play_style_scores?.power ?? 0.5,
      spin: ov.play_style_scores?.spin ?? a.play_style_scores?.spin ?? 0.5,
    });
  };

  const saveOverride = () => {
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const override = {};
    if (draft.weight_oz !== "") override.weight_oz = num(draft.weight_oz);
    if (draft.thickness_mm !== "") override.thickness_mm = num(draft.thickness_mm);
    if (draft.core) override.core = draft.core;
    if (draft.surface) override.surface = draft.surface;
    override.play_style_scores = {
      control: num(draft.control),
      power: num(draft.power),
      spin: num(draft.spin),
    };
    setConfig((c) => ({
      ...c,
      attribute_overrides: { ...c.attribute_overrides, [editingId]: override },
    }));
    setEditingId(null);
    toast.success("Override saved (remember to hit Save changes)");
  };

  const clearOverride = (pid) => {
    setConfig((c) => {
      const nc = { ...c.attribute_overrides };
      delete nc[pid];
      return { ...c, attribute_overrides: nc };
    });
    toast("Override cleared");
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, border: "1.5px solid var(--cd-border)", borderRadius: 10, padding: "0 12px" }}>
          <Search size={16} color="var(--cd-muted)" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search paddles by title…"
            data-testid="admin-search-input"
            style={{ border: 0, outline: "none", padding: "10px 0", width: "100%", fontSize: 14, background: "transparent" }}
          />
        </div>
        <button className="cd-btn-secondary" onClick={search} disabled={loading} data-testid="admin-search-btn">
          <RefreshCw size={14} /> {loading ? "Loading…" : "Search"}
        </button>
      </div>

      <div className="cd-micro" style={{ marginBottom: 8 }}>
        Showing {rows.length} paddles. Click a row to edit inferred attributes, or use the Hide toggle to block that specific product.
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {rows.map((p) => {
          const a = p.attributes || {};
          const hasOverride = !!config.attribute_overrides[p.product_id];
          const isBlocked = blockedSet.has(p.product_id);
          return (
            <div key={p.product_id} className="cd-card" style={{ padding: 12, opacity: isBlocked ? 0.55 : 1 }} data-testid={`admin-paddle-row-${p.product_id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {p.image && <img src={p.image} alt="" style={{ width: 48, height: 48, objectFit: "contain", background: "var(--cd-surface)", borderRadius: 8 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.title}
                  </div>
                  <div className="cd-micro" style={{ color: "var(--cd-muted)" }}>
                    {p.vendor} · ${p.price?.toFixed(2)} · {a.weight_oz ? `${a.weight_oz} oz` : "?"} · {a.thickness_mm ? `${a.thickness_mm}mm` : "?"} · c/p/s {a.play_style_scores ? `${a.play_style_scores.control?.toFixed(2)}/${a.play_style_scores.power?.toFixed(2)}/${a.play_style_scores.spin?.toFixed(2)}` : "?"}
                  </div>
                  <div className="cd-micro" style={{ fontFamily: "ui-monospace, monospace" }}>
                    ID: {p.product_id}
                    {hasOverride && <span className="cd-chip cd-chip-primary" style={{ marginLeft: 8, padding: "1px 6px", fontSize: 10 }}>overridden</span>}
                    {isBlocked && <span className="cd-chip cd-chip-danger" style={{ marginLeft: 8, padding: "1px 6px", fontSize: 10 }}>hidden</span>}
                  </div>
                </div>
                <button className="cd-btn-ghost" onClick={() => (editingId === p.product_id ? setEditingId(null) : startEdit(p))} data-testid={`admin-edit-${p.product_id}`}>
                  <Sliders size={14} /> Edit
                </button>
                <button className="cd-btn-ghost" onClick={() => toggleBlock(p.product_id)} data-testid={`admin-block-${p.product_id}`}>
                  <Ban size={14} /> {isBlocked ? "Unhide" : "Hide"}
                </button>
              </div>

              {editingId === p.product_id && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--cd-border)", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                  {[
                    ["Weight (oz)", "weight_oz", "number"],
                    ["Thickness (mm)", "thickness_mm", "number"],
                    ["Core", "core", "text"],
                    ["Surface", "surface", "text"],
                    ["Control 0–1", "control", "number"],
                    ["Power 0–1", "power", "number"],
                    ["Spin 0–1", "spin", "number"],
                  ].map(([label, key, type]) => (
                    <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span className="cd-micro">{label}</span>
                      <input
                        type={type}
                        step="0.01"
                        value={draft[key] ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                        data-testid={`override-${key}-${p.product_id}`}
                        style={{ padding: 8, border: "1.5px solid var(--cd-border)", borderRadius: 8, fontSize: 13 }}
                      />
                    </label>
                  ))}
                  <div style={{ display: "flex", gap: 8, gridColumn: "1 / -1", justifyContent: "flex-end" }}>
                    {hasOverride && (
                      <button className="cd-btn-ghost" onClick={() => clearOverride(p.product_id)}>
                        Clear override
                      </button>
                    )}
                    <button className="cd-btn-primary" onClick={saveOverride} data-testid={`override-save-${p.product_id}`}>
                      <Save size={14} /> Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Preview ----------

const PreviewTab = ({ token }) => {
  const [answers, setAnswers] = useState({ ...DEFAULT_ANSWERS });
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const data = await adminPreview(token, answers);
      setResults(data.recommendations || []);
    } catch {
      toast.error("Preview failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="cd-sub" style={{ marginBottom: 12 }}>
        Simulate any persona and see what your shoppers would see — using your latest saved config.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 16 }}>
        {QUESTIONS.map((q) => (
          <label key={q.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="cd-micro">{q.title}</span>
            <select
              value={answers[q.id]}
              onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
              data-testid={`preview-${q.id}`}
              style={{ padding: 8, border: "1.5px solid var(--cd-border)", borderRadius: 8, fontSize: 13 }}
            >
              {q.options.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </label>
        ))}
      </div>
      <button className="cd-btn-primary" onClick={run} disabled={busy} data-testid="preview-run-btn">
        <Eye size={14} /> {busy ? "Scoring…" : "Preview shopper results"}
      </button>

      <div style={{ marginTop: 20, display: "grid", gap: 10 }}>
        {results.map((r, i) => (
          <div key={r.product_id} className="cd-card" style={{ padding: 12, display: "flex", gap: 12 }} data-testid={`preview-result-${i}`}>
            {r.image && <img src={r.image} alt="" style={{ width: 60, height: 60, objectFit: "contain", background: "var(--cd-surface)", borderRadius: 8 }} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                #{i + 1}. {r.title}
                {r.pinned && <span className="cd-chip cd-chip-primary" style={{ marginLeft: 8 }}>Pinned</span>}
              </div>
              <div className="cd-micro">{r.vendor} · ${r.price?.toFixed(2)} · score {r.score}%</div>
              <div className="cd-micro">{r.reason}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Main ----------

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("vendors");
  const [config, setConfig] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [allPaddlesById, setAllPaddlesById] = useState(new Map());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await adminVerify(token);
        const [cfg, vs, pd] = await Promise.all([
          adminGetConfig(token),
          adminVendors(token),
          adminPaddles(token, { limit: 500 }),
        ]);
        setConfig(cfg);
        setVendors(vs);
        setAllPaddlesById(new Map((pd.paddles || []).map((p) => [p.product_id, p])));
        setReady(true);
      } catch {
        localStorage.removeItem(LS_KEY);
        setToken("");
      }
    })();
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await adminPutConfig(token, config);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(LS_KEY);
    setToken("");
    setReady(false);
  };

  if (!token) return <Login onOk={setToken} />;
  if (!ready || !config) return <div className="cd-container" data-testid="admin-loading"><div className="cd-skel" style={{ height: 160 }} /></div>;

  const tabs = [
    { id: "vendors", label: "Brands", icon: Ban },
    { id: "pins", label: "Pinned picks", icon: PinIcon },
    { id: "paddles", label: "Paddles", icon: Sliders },
    { id: "preview", label: "Preview", icon: Eye },
  ];

  return (
    <div className="cd-container-wide cd-fade-in" data-testid="admin-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <span className="cd-chip cd-chip-accent">Admin</span>
          <h1 className="cd-h1" style={{ margin: "10px 0 4px" }}>Recommendation controls</h1>
          <p className="cd-sub">Tune which paddles surface to shoppers. Changes save to the live quiz.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="cd-btn-primary" onClick={save} disabled={saving} data-testid="admin-save-btn">
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
          <button className="cd-btn-secondary" onClick={logout} data-testid="admin-logout-btn">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--cd-border)", marginBottom: 20, overflowX: "auto" }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            style={{
              padding: "10px 16px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              color: tab === t.id ? "var(--cd-primary)" : "var(--cd-muted)",
              borderBottom: `2px solid ${tab === t.id ? "var(--cd-primary)" : "transparent"}`,
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "vendors" && <VendorList vendors={vendors} config={config} setConfig={setConfig} />}
      {tab === "pins" && <PinsTab pins={config.pinned} setPins={(p) => setConfig((c) => ({ ...c, pinned: p }))} allPaddlesById={allPaddlesById} />}
      {tab === "paddles" && <PaddlesTab token={token} config={config} setConfig={setConfig} onLoaded={(rows) => setAllPaddlesById((m) => { const nm = new Map(m); rows.forEach((r) => nm.set(r.product_id, r)); return nm; })} />}
      {tab === "preview" && <PreviewTab token={token} />}
    </div>
  );
}
